"use client";

import React, {
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { cn } from "@/utils/utils";

const RIPPLE_DURATION = 0.8;
const ALWAYS_OFF_PROBABILITY = 0.6;
const RIPPLE_START_OFFSET = 0.2;
const DISTANCE_MULTIPLIER = 0.002;
const GLOW_DURATION_MIN = 2;
const GLOW_DURATION_RANGE = 3;
const GLOW_OFFSET_MULTIPLIER = 10;
const BREATHING_ALPHA_MIN = 0.4;
const BREATHING_ALPHA_RANGE = 1;
const BREATHING_SCALE_MIN = 0.5;
const BREATHING_SCALE_RANGE = 0.6;
const CLICK_ALPHA_BOOST = 0.5;
const CLICK_SCALE_BOOST = 0.5;
const ALPHA_THRESHOLD = 0.01;
const DEFAULT_COLOR: [number, number, number] = [163, 163, 163];

export interface DotPatternCanvasRef {
  triggerRipple: () => void;
}

interface DotPatternCanvasProps extends React.CanvasHTMLAttributes<HTMLCanvasElement> {
  width?: number;
  height?: number;
  cx?: number;
  cy?: number;
  cr?: number;
  className?: string;
  glow?: boolean;
  color?: string;
  maxOpacity?: number;
}

interface DotData {
  x: number;
  y: number;
  row: number;
  col: number;
  rippleDelay: number;
  glowDuration: number;
  glowOffset: number;
}

const parseColor = (colorStr: string): [number, number, number] => {
  if (colorStr.startsWith("#")) {
    const hex = colorStr.slice(1);
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  if (colorStr.startsWith("rgb")) {
    const matches = colorStr.match(/\d+/g);
    if (matches && matches.length >= 3) {
      return [
        parseInt(matches[0], 10),
        parseInt(matches[1], 10),
        parseInt(matches[2], 10),
      ];
    }
  }
  return DEFAULT_COLOR;
};

const easeInOut = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

const seededRandom = (row: number, col: number) => {
  const sin = Math.sin(row * 12.9898 + col * 78.233) * 43758.5453;
  return sin - Math.floor(sin);
};

export const DotPatternCanvas = forwardRef<
  DotPatternCanvasRef,
  DotPatternCanvasProps
>(
  (
    {
      width = 10,
      height = 10,
      cx = 1,
      cy = 1,
      cr = 0.8,
      className,
      glow = false,
      color = "#a3a3a3",
      maxOpacity = 0.75,
      ...props
    },
    ref,
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const animationFrameRef = useRef<number | undefined>(undefined);
    const dotsRef = useRef<DotData[]>([]);

    const dimensionsRef = useRef({ w: 0, h: 0 });
    const introRippleStartRef = useRef<number>(-1);
    const clickRippleStartRef = useRef<number>(-1);
    const hasTriggeredIntro = useRef(false);

    useImperativeHandle(ref, () => ({
      triggerRipple: () => {
        clickRippleStartRef.current = Date.now() / 1000 - RIPPLE_START_OFFSET;
      },
    }));

    const colorRgba = useMemo(() => {
      const [r, g, b] = parseColor(color);
      return `rgba(${r}, ${g}, ${b}, 1)`;
    }, [color]);

    const calculateDots = useCallback(
      (w: number, h: number) => {
        if (w === 0 || h === 0) {
          dotsRef.current = [];
          return;
        }

        const cols = Math.ceil(w / width);
        const rows = Math.ceil(h / height);
        const centerX = w / 2;
        const centerY = h / 2;

        const dots: DotData[] = [];

        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const x = w - (col * width + cx);
            const y = row * height + cy;

            if (x < -width || y < -height) continue;

            const distance = Math.sqrt(
              Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2),
            );

            if (seededRandom(row, col) >= ALWAYS_OFF_PROBABILITY) {
              dots.push({
                x,
                y,
                row,
                col,
                rippleDelay: distance * DISTANCE_MULTIPLIER,
                glowDuration:
                  seededRandom(row, col) * GLOW_DURATION_RANGE +
                  GLOW_DURATION_MIN,
                glowOffset: seededRandom(row, col + 1) * GLOW_OFFSET_MULTIPLIER,
              });
            }
          }
        }
        dotsRef.current = dots;
      },
      [width, height, cx, cy],
    );

    useEffect(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const ctx = canvas.getContext("2d", { alpha: true });
      if (!ctx) return;

      const draw = () => {
        const now = Date.now() / 1000;
        const currentRect = container.getBoundingClientRect();

        if (currentRect.width === 0 || currentRect.height === 0) {
          animationFrameRef.current = requestAnimationFrame(draw);
          return;
        }

        if (
          currentRect.width !== dimensionsRef.current.w ||
          currentRect.height !== dimensionsRef.current.h
        ) {
          dimensionsRef.current = {
            w: currentRect.width,
            h: currentRect.height,
          };

          const dpr = window.devicePixelRatio || 1;
          const canvasWidth = currentRect.width * dpr;
          const canvasHeight = currentRect.height * dpr;

          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          canvas.style.width = `${currentRect.width}px`;
          canvas.style.height = `${currentRect.height}px`;

          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

          calculateDots(currentRect.width, currentRect.height);

          if (
            glow &&
            !hasTriggeredIntro.current &&
            dotsRef.current.length > 0
          ) {
            introRippleStartRef.current = now - RIPPLE_START_OFFSET;
            hasTriggeredIntro.current = true;
          }
        }

        ctx.clearRect(0, 0, dimensionsRef.current.w, dimensionsRef.current.h);
        ctx.fillStyle = colorRgba;

        if (!glow) {
          ctx.globalAlpha = maxOpacity;
          for (const dot of dotsRef.current) {
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, cr, 0, Math.PI * 2);
            ctx.fill();
          }
          animationFrameRef.current = requestAnimationFrame(draw);
          return;
        }

        const introStart = introRippleStartRef.current;
        const clickStart = clickRippleStartRef.current;

        for (const dot of dotsRef.current) {
          const time = now + dot.glowOffset;
          const glowTime = time % dot.glowDuration;
          const t = glowTime / dot.glowDuration;
          const glowProgress =
            t < 0.5 ? easeInOut(t * 2) : easeInOut((1 - t) * 2);

          let alpha =
            maxOpacity *
            (BREATHING_ALPHA_MIN + glowProgress * BREATHING_ALPHA_RANGE);
          let scale =
            BREATHING_SCALE_MIN + glowProgress * BREATHING_SCALE_RANGE;

          if (introStart > 0) {
            const introElapsed = now - introStart;
            const introDelay = dot.rippleDelay;

            if (introElapsed < introDelay) {
              alpha = 0;
            } else if (introElapsed < introDelay + RIPPLE_DURATION) {
              const introProgress =
                (introElapsed - introDelay) / RIPPLE_DURATION;
              const introEase = easeInOut(introProgress);
              alpha = alpha * introEase;
              scale = scale * introEase;
            }
          }

          if (clickStart > 0 && alpha > 0) {
            const clickElapsed = now - clickStart;
            const clickDelay = dot.rippleDelay;

            if (
              clickElapsed > clickDelay &&
              clickElapsed < clickDelay + RIPPLE_DURATION
            ) {
              const clickProgress =
                (clickElapsed - clickDelay) / RIPPLE_DURATION;
              const waveIntensity = Math.sin(clickProgress * Math.PI);

              alpha += waveIntensity * CLICK_ALPHA_BOOST;
              scale += waveIntensity * CLICK_SCALE_BOOST;
            }
          }

          if (alpha <= ALPHA_THRESHOLD) continue;

          ctx.globalAlpha = Math.min(1, alpha);

          ctx.beginPath();
          ctx.arc(dot.x, dot.y, cr * scale, 0, Math.PI * 2);
          ctx.fill();
        }

        animationFrameRef.current = requestAnimationFrame(draw);
      };

      draw();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, [glow, colorRgba, maxOpacity, cr, calculateDots]);

    return (
      <div
        ref={containerRef}
        className={cn(
          "pointer-events-none absolute inset-0 h-full w-full",
          className,
        )}
      >
        <canvas ref={canvasRef} className="block" {...props} />
      </div>
    );
  },
);

DotPatternCanvas.displayName = "DotPatternCanvas";
